import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Badge,
} from '@chakra-ui/react';

import { Empty } from '@grcs/views/shared/components/ui/Empty';
import { LoadingSpinner } from '@grcs/views/shared/components/ui/LoadingSpinner';
import { useData } from '@grcs/context/DataContext';
import { useAnalyticsService } from '@grcs/hook/useAnalyticsService';
import { TABLE_NAMES } from '@grcs/utils/database/types';
import {
  HomeworkV2Profile,
  HomeworkStudentProfile,
  StudentProfile,
} from '@grcs/types/DataTypes';

interface StudentAdvancedAnalyticsProps {
  active: boolean;
}

type StudentName = {
  id: string;
  name: string;
  class: string;
};

type ClassInfo = {
  class_id: string;
  class_name: string;
  semester_year: string;
};

type ArticleInfo = {
  id: string;
  title: string;
  grade: string;
  chapter: string;
  word_count: number | null;
};

type HomeworkReadingDetail = {
  homework_token: string;
  article_token: string;
  article_title: string;
  semester_year: string;
  word_count: number;
  total_time: number;
  time_minutes: number;
  wpm: number;
  class_name: string;
};

type StudentSemesterSummary = {
  semester_year: string;
  average_wpm: number;
  homework_count: number;
  details: HomeworkReadingDetail[];
};

type StudentRow = {
  student: StudentName;
  summaries: Map<string, StudentSemesterSummary>;
};

type CachedStudentRow = {
  student: StudentName;
  summaries: [string, StudentSemesterSummary][];
};

const toFullName = (student: StudentProfile) =>
  `${student.first_name} ${student.last_name}`.trim();

const DEBUG_ARTICLE_ID = '8bfe03fe-9434-4854-ab69-d7555be6295c';

const calculateWpm = (wordCount: number, totalTimeMs: number) => {
  if (!wordCount || !totalTimeMs) return 0;
  const minutes = totalTimeMs / 60000;
  if (minutes <= 0) return 0;
  return Math.round((wordCount / minutes) * 10) / 10;
};

const parseClassName = (className: string | null | undefined) => {
  if (!className)
    return { letterPrefix: '', numPrefix: 0, numSuffix: 0, hasSuffix: false };
  const match = className.match(/^([A-Z])(\d+)(?:-(\d+))?$/);
  if (!match)
    return { letterPrefix: '', numPrefix: 0, numSuffix: 0, hasSuffix: false };

  const letterPrefix = match[1];
  const numPrefix = parseInt(match[2], 10);
  const numSuffix = match[3] ? parseInt(match[3], 10) : 0;
  const hasSuffix = !!match[3];

  return { letterPrefix, numPrefix, numSuffix, hasSuffix };
};

const compareClassName = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => {
  const aData = parseClassName(a);
  const bData = parseClassName(b);

  // Compare letter prefix
  if (aData.letterPrefix !== bData.letterPrefix) {
    return aData.letterPrefix.localeCompare(bData.letterPrefix);
  }

  // Compare numeric prefix
  if (aData.numPrefix !== bData.numPrefix) {
    return aData.numPrefix - bData.numPrefix;
  }

  // If numeric prefix is the same, handle suffix
  // Classes without suffix (e.g., M4) come before classes with suffix (e.g., M4-1)
  if (!aData.hasSuffix && bData.hasSuffix) return -1;
  if (aData.hasSuffix && !bData.hasSuffix) return 1;

  // Compare numeric suffix if both have suffixes
  return aData.numSuffix - bData.numSuffix;
};

export const StudentAdvancedAnalytics: React.FC<
  StudentAdvancedAnalyticsProps
> = ({ active }) => {
  const { supabase } = useData();
  const analyticsService = useAnalyticsService();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<StudentRow[]>([]);
  const [semesterYears, setSemesterYears] = useState<string[]>([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [totalClassesByYear, setTotalClassesByYear] = useState<
    Map<string, number>
  >(new Map());
  const [totalHomeworkBySemesterClass, setTotalHomeworkBySemesterClass] =
    useState<Map<string, Map<string, number>>>(new Map());
  const [totalHomeworkByStudent, setTotalHomeworkByStudent] = useState<
    Map<string, number>
  >(new Map());
  const [totalHomework, setTotalHomework] = useState<HomeworkV2Profile[]>([]);
  const [cachedAt, setCachedAt] = useState<Date | null>(null);
  const [refreshRequested, setRefreshRequested] = useState(false);

  const loadingRef = useRef(false);
  const cacheRef = useRef<{
    rows: StudentRow[];
    semesterYears: string[];
    totalStudents: number;
    totalClassesByYear: Map<string, number>;
    totalHomeworkBySemesterClass: Map<string, Map<string, number>>;
    totalHomeworkByStudent: Map<string, number>;
    totalHomework: HomeworkV2Profile[];
    cachedAt: Date;
  } | null>(null);
  const localCacheKey = 'studentAdvancedAnalyticsCache';
  useEffect(() => {
    if (!active || loadingRef.current) {
      return;
    }

    const loadAdvancedAnalytics = async () => {
      if (!cacheRef.current) {
        try {
          const cachedRaw = localStorage.getItem(localCacheKey);
          if (cachedRaw) {
            const parsed = JSON.parse(cachedRaw) as {
              rows: CachedStudentRow[];
              semesterYears: string[];
              totalStudents: number;
              totalClassesByYear: [string, number][];
              totalHomeworkBySemesterClass: [string, [string, number][]][];
              totalHomeworkByStudent: [string, number][];
              totalHomework: HomeworkV2Profile[];
              cachedAt: string;
            };
            const restoredRows: StudentRow[] = parsed.rows.map((row) => ({
              student: row.student,
              summaries: new Map(row.summaries),
            }));
            cacheRef.current = {
              rows: restoredRows,
              semesterYears: parsed.semesterYears,
              totalStudents: parsed.totalStudents,
              totalClassesByYear: new Map(parsed.totalClassesByYear),
              totalHomeworkBySemesterClass: new Map(
                parsed.totalHomeworkBySemesterClass.map(
                  ([semester, entries]) => [semester, new Map(entries)],
                ),
              ),
              totalHomeworkByStudent: new Map(parsed.totalHomeworkByStudent),
              totalHomework: parsed.totalHomework,
              cachedAt: new Date(parsed.cachedAt),
            };
          }
        } catch (error) {
          console.warn('Failed to read cached student analytics:', error);
        }
      }
      if (cacheRef.current && !refreshRequested) {
        const cached = cacheRef.current;
        console.log(
          '[StudentAdvancedAnalytics] Ignoring cached analytics data and fetching fresh data. Cached at:',
          cached.cachedAt,
        );
        setCachedAt(cached.cachedAt);
      }
      loadingRef.current = true;
      setLoading(true);

      try {
        const {
          students: studentsData,
          classes: classesData,
          articles: articlesData,
          homework: homeworkInfoData,
          homeworkRelations: homeworkData,
        } = await analyticsService.getStudentAdvancedAnalyticsDataset();
        const debugArticleRelationsResult = await supabase
          .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
          .select('*')
          .eq('article_token', DEBUG_ARTICLE_ID);
        const debugHomeworkTokens = Array.from(
          new Set(
            (
              (debugArticleRelationsResult.data as HomeworkStudentProfile[]) ||
              []
            ).map((relation) => relation.homework_token),
          ),
        );
        const debugHomeworkRowsByTokenResult =
          debugHomeworkTokens.length > 0
            ? await supabase
                .from(TABLE_NAMES.HOMEWORK)
                .select('*')
                .in('homework_token', debugHomeworkTokens)
            : { data: [], error: null };
        const debugStudentTokens = Array.from(
          new Set(
            (
              (debugArticleRelationsResult.data as HomeworkStudentProfile[]) ||
              []
            ).map((relation) => relation.student_token),
          ),
        );
        const debugStudentCacheResult =
          debugStudentTokens.length > 0
            ? await supabase
                .from(TABLE_NAMES.HOMEWORK_STUDENT_CACHE)
                .select('*')
                .in('student_token', debugStudentTokens)
            : { data: [], error: null };
        const debugArticleCacheResult = await supabase
          .from(TABLE_NAMES.HOMEWORK_ARTICLE_CACHE)
          .select('*')
          .eq('article_token', DEBUG_ARTICLE_ID);

        console.log('Fetched advanced analytics data', {
          studentsCount: studentsData.length,
          classesCount: classesData.length,
          articlesCount: articlesData.length,
          homeworkRelationsCount: homeworkData.length,
          homeworkInfoCount: homeworkInfoData.length,
        });
        if (debugArticleRelationsResult.error) {
          console.warn(
            '[StudentAdvancedAnalytics] Failed to fetch debug article relations',
            {
              articleId: DEBUG_ARTICLE_ID,
              error: debugArticleRelationsResult.error,
            },
          );
        }
        if (debugHomeworkRowsByTokenResult.error) {
          console.warn(
            '[StudentAdvancedAnalytics] Failed to fetch debug homework rows by relation tokens',
            {
              articleId: DEBUG_ARTICLE_ID,
              homeworkTokens: debugHomeworkTokens,
              error: debugHomeworkRowsByTokenResult.error,
            },
          );
        }
        if (debugStudentCacheResult.error) {
          console.warn(
            '[StudentAdvancedAnalytics] Failed to fetch debug homework student cache rows',
            {
              articleId: DEBUG_ARTICLE_ID,
              studentTokens: debugStudentTokens,
              error: debugStudentCacheResult.error,
            },
          );
        }
        if (debugArticleCacheResult.error) {
          console.warn(
            '[StudentAdvancedAnalytics] Failed to fetch debug homework article cache row',
            {
              articleId: DEBUG_ARTICLE_ID,
              error: debugArticleCacheResult.error,
            },
          );
        }
        setTotalHomework(homeworkInfoData);

        const studentMap = new Map<string, StudentName>();
        studentsData.forEach((student) => {
          studentMap.set(student.student_token, {
            id: student.student_token,
            name: toFullName(student),
            class: student.class_name,
          });
        });
        setTotalStudents(studentMap.size);

        const classMapBySemester = new Map<string, Map<string, ClassInfo>>();
        const classMapById = new Map<string, ClassInfo>();
        const sortedClasses = [...classesData].sort((a, b) => {
          const semesterCompare = (a.semester_year || '').localeCompare(
            b.semester_year || '',
          );
          if (semesterCompare !== 0) return semesterCompare;
          return (a.class_token || '').localeCompare(b.class_token || '');
        });
        sortedClasses.forEach((classItem) => {
          const semesterKey = classItem.semester_year || 'Unknown';
          if (!classMapBySemester.has(semesterKey)) {
            classMapBySemester.set(semesterKey, new Map());
          }
          const info: ClassInfo = {
            class_id: classItem.class_token,
            class_name: classItem.class_name,
            semester_year: semesterKey,
          };
          classMapBySemester.get(semesterKey)?.set(classItem.class_token, info);
          classMapById.set(classItem.class_token, info);
        });

        const sortedSemesterYears = Array.from(classMapBySemester.keys()).sort(
          (a, b) => a.localeCompare(b),
        );
        const classesCountByYear = new Map<string, number>();
        classMapBySemester.forEach((map, year) => {
          classesCountByYear.set(year, map.size);
        });
        setTotalClassesByYear(classesCountByYear);

        const sortedArticles = [...articlesData].sort((a, b) =>
          (a.id || '').localeCompare(b.id || ''),
        );
        const articleMap = new Map<string, ArticleInfo>();
        sortedArticles.forEach((article) => {
          articleMap.set(article.id, {
            id: article.id,
            title: article.title,
            grade: article.grade,
            chapter: article.chapter,
            word_count: article.word_count ?? null,
          });
        });
        const debugArticleRelations =
          (debugArticleRelationsResult.data as HomeworkStudentProfile[]) || [];
        const debugHomeworkRows = homeworkInfoData.filter(
          (homework) => homework.article_token === DEBUG_ARTICLE_ID,
        );
        const debugHomeworkRowsByToken =
          debugHomeworkRowsByTokenResult.data || [];
        const foundHomeworkTokenSet = new Set(
          debugHomeworkRowsByToken.map(
            (homework: HomeworkV2Profile) => homework.homework_token,
          ),
        );
        const missingHomeworkTokens = debugHomeworkTokens.filter(
          (homeworkToken) => !foundHomeworkTokenSet.has(homeworkToken),
        );
        const debugStudentCacheRows = debugStudentCacheResult.data || [];
        const cacheRowsWithArticle = debugStudentCacheRows.filter(
          (cacheRow: any) =>
            JSON.stringify(cacheRow.data || '').includes(DEBUG_ARTICLE_ID),
        );
        const debugArticleCacheRows = debugArticleCacheResult.data || [];
        const articleCachePayload = JSON.stringify(
          debugArticleCacheRows.map((cacheRow: any) => cacheRow.data) || [],
        );
        const relationHomeworkTokensMissingFromArticleCache =
          debugHomeworkTokens.filter(
            (homeworkToken) => !articleCachePayload.includes(homeworkToken),
          );
        console.log('[StudentAdvancedAnalytics] Debug article relation scan', {
          articleId: DEBUG_ARTICLE_ID,
          article: articleMap.get(DEBUG_ARTICLE_ID) || null,
          homeworkRowsCount: debugHomeworkRows.length,
          homeworkRows: debugHomeworkRows,
          homeworkTokensFromRelations: debugHomeworkTokens,
          homeworkRowsByRelationTokenCount: debugHomeworkRowsByToken.length,
          homeworkRowsByRelationToken: debugHomeworkRowsByToken,
          missingHomeworkTokens,
          relationsCount: debugArticleRelations.length,
          relations: debugArticleRelations.map((relation) => ({
            ...relation,
            student: studentMap.get(relation.student_token) || null,
            classInfo: classMapById.get(relation.class_token) || null,
          })),
          affectedStudentTokens: debugStudentTokens,
          studentCacheRowsCount: debugStudentCacheRows.length,
          studentCacheRowsWithArticleCount: cacheRowsWithArticle.length,
          studentCacheRowsWithArticle: cacheRowsWithArticle,
          articleCacheRowsCount: debugArticleCacheRows.length,
          articleCacheRows: debugArticleCacheRows,
          relationHomeworkTokensMissingFromArticleCache,
        });

        const homeworkByStudent = new Map<string, HomeworkStudentProfile[]>();
        const homeworkCountByClass = new Map<string, number>();
        const homeworkUniqueBySemesterClass = new Map<
          string,
          Map<string, Set<string>>
        >();
        const homeworkCountByStudent = new Map<string, number>();
        homeworkData.forEach((relation) => {
          if (!homeworkByStudent.has(relation.student_token)) {
            homeworkByStudent.set(relation.student_token, []);
          }
          homeworkByStudent.get(relation.student_token)?.push(relation);
          if (relation.completed) {
            homeworkCountByClass.set(
              relation.class_token,
              (homeworkCountByClass.get(relation.class_token) || 0) + 1,
            );
            const classInfo = classMapById.get(relation.class_token);
            const semesterKey = classInfo?.semester_year || 'Unknown';
            const className = classInfo?.class_name || 'Unknown';
            if (!homeworkUniqueBySemesterClass.has(semesterKey)) {
              homeworkUniqueBySemesterClass.set(
                semesterKey,
                new Map<string, Set<string>>(),
              );
            }
            const classMap = homeworkUniqueBySemesterClass.get(semesterKey);
            if (classMap && !classMap.has(className)) {
              classMap.set(className, new Set());
            }
            classMap?.get(className)?.add(relation.homework_token);
            homeworkCountByStudent.set(
              relation.student_token,
              (homeworkCountByStudent.get(relation.student_token) || 0) + 1,
            );
          }
        });
        const totalBySemesterClass = new Map<string, Map<string, number>>();
        homeworkUniqueBySemesterClass.forEach((classMap, semester) => {
          const counts = new Map<string, number>();
          classMap.forEach((homeworkSet, className) => {
            counts.set(className, homeworkSet.size);
          });
          totalBySemesterClass.set(semester, counts);
        });
        setTotalHomeworkBySemesterClass(totalBySemesterClass);
        setTotalHomeworkByStudent(homeworkCountByStudent);

        const perYearArticle = new Map<string, Set<string>>();
        const studentRows: StudentRow[] = [];
        studentMap.forEach((student) => {
          const studentHomework = homeworkByStudent.get(student.id) || [];
          const summaries = new Map<string, StudentSemesterSummary>();

          studentHomework.forEach((relation) => {
            if (!relation.completed) return;
            const classInfo = classMapById.get(relation.class_token);
            if (!classInfo) return;
            const semesterYear = classInfo.semester_year || 'Unknown';
            const articleInfo = relation.article_token
              ? articleMap.get(relation.article_token)
              : undefined;
            if (!articleInfo) return;

            if (!perYearArticle.has(semesterYear)) {
              perYearArticle.set(semesterYear, new Set<string>());
            }
            perYearArticle.get(semesterYear)?.add(relation.article_token);

            const totalTime = relation.total_time || 0;
            const wordCount = articleInfo.word_count || 0;
            const wpm = calculateWpm(wordCount, totalTime);
            const timeMinutes = Math.round((totalTime / 60000) * 10) / 10;

            const detail: HomeworkReadingDetail = {
              homework_token: relation.homework_token,
              article_token: relation.article_token,
              article_title: articleInfo.title,
              semester_year: semesterYear,
              word_count: wordCount,
              total_time: totalTime,
              time_minutes: timeMinutes,
              wpm,
              class_name: classInfo.class_name,
            };

            if (!summaries.has(semesterYear)) {
              summaries.set(semesterYear, {
                semester_year: semesterYear,
                average_wpm: 0,
                homework_count: 0,
                details: [],
              });
            }

            const summary = summaries.get(semesterYear);
            if (summary) {
              summary.details.push(detail);
              summary.homework_count += 1;
            }
          });

          summaries.forEach((summary) => {
            const detailsWithWpm = summary.details.filter(
              (item) => item.wpm > 0,
            );
            const total = detailsWithWpm.reduce(
              (sum, item) => sum + item.wpm,
              0,
            );
            summary.average_wpm =
              detailsWithWpm.length > 0
                ? Math.round((total / detailsWithWpm.length) * 10) / 10
                : 0;
          });

          studentRows.push({
            student,
            summaries,
          });
        });

        setRows(studentRows);
        setSemesterYears(sortedSemesterYears);
        console.log('articles by years ', perYearArticle);
        const cacheTime = new Date();
        cacheRef.current = {
          rows: studentRows,
          semesterYears: sortedSemesterYears,
          totalStudents: studentMap.size,
          totalClassesByYear: classesCountByYear,
          totalHomeworkBySemesterClass: totalBySemesterClass,
          totalHomeworkByStudent: homeworkCountByStudent,
          totalHomework: homeworkInfoData,
          cachedAt: cacheTime,
        };
        try {
          const cachedRows: CachedStudentRow[] = studentRows.map((row) => ({
            student: row.student,
            summaries: Array.from(row.summaries.entries()),
          }));
          localStorage.setItem(
            localCacheKey,
            JSON.stringify({
              rows: cachedRows,
              semesterYears: sortedSemesterYears,
              totalStudents: studentMap.size,
              totalClassesByYear: Array.from(classesCountByYear.entries()),
              totalHomeworkBySemesterClass: Array.from(
                totalBySemesterClass.entries(),
              ).map(([semester, classMap]) => [
                semester,
                Array.from(classMap.entries()),
              ]),
              totalHomeworkByStudent: Array.from(
                homeworkCountByStudent.entries(),
              ),
              totalHomework: homeworkInfoData,
              cachedAt: cacheTime.toISOString(),
            }),
          );
        } catch (error) {
          console.warn('Failed to save cached student analytics:', error);
        }
        setCachedAt(cacheTime);
        setRefreshRequested(false);
        console.log('Student Advanced Analytics Summary', {
          totalStudents: studentMap.size,
          totalClassesByYear: Array.from(classesCountByYear.entries()),
          totalHomeworkByClass: Array.from(homeworkCountByClass.entries()),
          totalHomeworkBySemesterClass: Array.from(
            totalBySemesterClass.entries(),
          ).map(([semester, classMap]) => [
            semester,
            Array.from(classMap.entries()),
          ]),
          totalHomeworkByStudent: Array.from(homeworkCountByStudent.entries()),
          totalArticles: Array.from(articleMap.entries()),
          totalPerYearArticle: Array.from(perYearArticle.entries()).map(
            ([year, count]) => [year, count],
          ),
          totalHomework: homeworkInfoData.length,
        });
      } catch (error) {
        console.error('Failed to load advanced student analytics:', error);
        setRows([]);
        setSemesterYears([]);
        setTotalStudents(0);
        setTotalClassesByYear(new Map());
        setTotalHomeworkBySemesterClass(new Map());
        setTotalHomeworkByStudent(new Map());
        setTotalHomework([]);
        setRefreshRequested(false);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    };

    loadAdvancedAnalytics();
  }, [active, supabase, analyticsService, refreshRequested]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) =>
      a.student.name.localeCompare(b.student.name),
    );
  }, [rows]);

  const sortedRowsByClass = useMemo(() => {
    return [...rows].sort((a, b) => {
      const classCompare = compareClassName(a.student.class, b.student.class);
      if (classCompare !== 0) return classCompare;
      return a.student.name.localeCompare(b.student.name);
    });
  }, [rows]);

  const handleDownloadCsv = () => {
    const sortedHomework = [...totalHomework].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateA - dateB;
    });
    const header = [
      'Student Name',
      'Class Name',
      ...sortedHomework.map((homework) => homework.title),
    ];

    const lines = [header.join(',')];

    const createdTimeRow = [
      '',
      '',
      ...sortedHomework.map((entry) => entry.created_at.split('T')[0]),
    ];

    lines.push(createdTimeRow.join(','));

    sortedRowsByClass.forEach((row) => {
      const wpmByHomework = new Map<string, number>();
      row.summaries.forEach((summary) => {
        summary.details.forEach((detail) => {
          wpmByHomework.set(detail.homework_token, detail.wpm);
        });
      });
      const rowValues = [
        row.student.name,
        row.student.class,
        ...Array.from(totalHomework).map((homework) => {
          const entry = wpmByHomework.get(homework.homework_token);
          if (!entry) {
            return '';
          }
          return entry;
        }),
      ];

      lines.push(rowValues.join(','));
    });

    // Filter out columns that don't have any values
    const firstLineColumns = lines[0].split(',').length;
    const columnsWithData = new Set<number>();

    // Always keep first two columns (Student Name, Class Name)
    columnsWithData.add(0);
    columnsWithData.add(1);

    // Check which other columns have at least one non-empty value
    for (let colIndex = 2; colIndex < firstLineColumns; colIndex++) {
      for (let rowIndex = 2; rowIndex < lines.length; rowIndex++) {
        const value = lines[rowIndex].split(',')[colIndex]?.trim();
        if (value && value !== '') {
          columnsWithData.add(colIndex);
          break;
        }
      }
    }

    // Filter lines to only keep columns with data
    const filteredLines = lines.map((line) => {
      const columns = line.split(',');
      return columns.filter((_, index) => columnsWithData.has(index)).join(',');
    });

    const csvContent = filteredLines.join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'student-advanced-analytics.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <LoadingSpinner message="Loading advanced student analytics..." />;
  }

  if (sortedRows.length === 0) {
    return <Empty>No advanced student analytics available</Empty>;
  }

  return (
    <VStack spacing={6} align="stretch">
      <Text fontSize="lg" fontWeight="600">
        Student Advanced Reading Analytics
      </Text>

      <HStack justify="space-between" align="center">
        <Text fontSize="sm" color="gray.600">
          {cachedAt ? `Cached at ${cachedAt.toLocaleString()}` : 'Not cached'}
        </Text>
        <HStack spacing={2}>
          <Button size="sm" colorScheme="teal" onClick={handleDownloadCsv}>
            Download CSV
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => {
              cacheRef.current = null;
              setCachedAt(null);
              try {
                localStorage.removeItem(localCacheKey);
              } catch (error) {
                console.warn(
                  'Failed to clear cached student analytics:',
                  error,
                );
              }
              setRefreshRequested(true);
            }}
          >
            Update
          </Button>
        </HStack>
      </HStack>

      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between" align="center">
          <Text fontSize="sm" color="gray.600">
            Total students: {totalStudents}
          </Text>
        </HStack>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Semester Year</Th>
              <Th isNumeric>Total Classes</Th>
            </Tr>
          </Thead>
          <Tbody>
            {semesterYears.map((year) => (
              <Tr key={year}>
                <Td>{year}</Td>
                <Td isNumeric>{totalClassesByYear.get(year) || 0}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Semester</Th>
              <Th>Class</Th>
              <Th isNumeric>Total Homework</Th>
            </Tr>
          </Thead>
          <Tbody>
            {semesterYears.flatMap((year) => {
              const classMap = totalHomeworkBySemesterClass.get(year);
              if (!classMap) {
                return [
                  <Tr key={`${year}-empty`}>
                    <Td>{year}</Td>
                    <Td>--</Td>
                    <Td isNumeric>0</Td>
                  </Tr>,
                ];
              }
              return Array.from(classMap.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([className, count], index) => (
                  <Tr key={`${year}-${className}`}>
                    <Td>{index === 0 ? year : ''}</Td>
                    <Td>{className}</Td>
                    <Td isNumeric>{count}</Td>
                  </Tr>
                ));
            })}
          </Tbody>
        </Table>
      </VStack>

      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Student</Th>
            {semesterYears.map((year) => (
              <Th key={year}>{year}</Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {sortedRows.map((row) => (
            <Tr key={row.student.id}>
              <Td>
                <VStack align="start" spacing={1}>
                  <Text>{row.student.name}</Text>
                  <Text fontSize="xs" color="gray.500">
                    Total homework:{' '}
                    {totalHomeworkByStudent.get(row.student.id) || 0}
                  </Text>
                </VStack>
              </Td>
              {semesterYears.map((year) => {
                const summary = row.summaries.get(year);
                if (!summary) {
                  return <Td key={year}>--</Td>;
                }

                return (
                  <Td key={year}>
                    <Popover trigger="hover" placement="auto" openDelay={200}>
                      <PopoverTrigger>
                        <Box cursor="pointer">
                          <Text fontWeight="medium">
                            {summary.average_wpm} wpm
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {summary.homework_count} homework
                          </Text>
                        </Box>
                      </PopoverTrigger>
                      <PopoverContent maxW="720px" w="max-content">
                        <PopoverArrow />
                        <PopoverHeader fontWeight="semibold">
                          {row.student.name} • {year}
                        </PopoverHeader>
                        <PopoverBody overflowX="auto">
                          <VStack align="stretch" spacing={3} minW="640px">
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                Avg {summary.average_wpm} wpm
                              </Text>
                              <Badge colorScheme="blue">
                                {summary.homework_count} completed
                              </Badge>
                            </HStack>
                            <Table size="sm" variant="simple">
                              <Thead>
                                <Tr>
                                  <Th>Article</Th>
                                  <Th>Word Count</Th>
                                  <Th>Class</Th>
                                  <Th isNumeric>Time (min)</Th>
                                  <Th isNumeric>WPM</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {summary.details.map((detail, index) => (
                                  <Tr
                                    key={`${detail.homework_token}-${detail.article_title}-${detail.class_name}-${index}`}
                                  >
                                    <Td>{detail.article_title}</Td>
                                    <Td>{detail.word_count}</Td>
                                    <Td>{detail.class_name}</Td>
                                    <Td isNumeric>{detail.time_minutes}</Td>
                                    <Td isNumeric>{detail.wpm}</Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </VStack>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </VStack>
  );
};
